import { Controller, Control, FieldValues, RegisterOptions, Path } from 'react-hook-form';
import { StyledAutocomplete } from './Form.styles';
import { TextField } from '@mui/material';
import { useState, type Dispatch, type SetStateAction, type SyntheticEvent } from 'react';

type OptionsProp = { name: string; id: string };

type FormAutocompleteProps<T extends FieldValues = FieldValues> = {
  name: Path<T>;
  control?: Control<T>;
  label: string;
  testId?: string;
  options: OptionsProp[];
  disabled?: boolean;
  defaultValue?: string;
  rules?: RegisterOptions<T, Path<T>>;
  dataScrollRef?: (node?: Element | null | undefined) => void;
  setSearchTerm?: Dispatch<SetStateAction<string>>;

  forceBottomPlacement?: boolean;
  className?: string;
};

export const FormAutocomplete = <T extends FieldValues = FieldValues>({
  name,
  control,
  label,
  testId,
  options = [],
  disabled,
  rules,
  dataScrollRef,
  setSearchTerm,
  forceBottomPlacement,
  className,
}: FormAutocompleteProps<T>) => {
  const [hasInputText, setHasInputText] = useState(false);

  return (
    <Controller<T>
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        return (
          <StyledAutocomplete
            className={className}
            value={options.find((option) => option.id === field.value) ?? null}
            disabled={disabled}
            onBlur={field.onBlur}
            onInputChange={(_event, inputValue, reason) => setHasInputText(reason === 'input' && inputValue !== '')}
            ref={field.ref}
            data-testid={testId}
            id={`${name}-option`}
            getOptionLabel={(option: { name: string }) => option?.name}
            isOptionEqualToValue={(option: { id: string }, value: { id: string }) => option?.id === value?.id}
            options={options}
            componentsProps={
              forceBottomPlacement
                ? {
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [{ name: 'flip', enabled: false }],
                      className: 'form-autocomplete-forced-bottom-popper',
                    },
                  }
                : undefined
            }
            renderInput={(params) => (
              <TextField
                ref={field.ref}
                {...params}
                label={label}
                name={field.name}
                error={!!error && !hasInputText}
                helperText={hasInputText ? undefined : error?.message}
                FormHelperTextProps={{ role: 'alert', 'aria-label': `alert-${field.name}` }}
                onChange={(e) => {
                  if (setSearchTerm) setSearchTerm(e.target.value);
                }}
              />
            )}
            onChange={(_event: SyntheticEvent<Element, Event>, newValue: OptionsProp) =>
              field.onChange(newValue?.id ?? null)
            }
            renderOption={(props, option: OptionsProp) => {
              return (
                <li {...props} ref={dataScrollRef} key={option?.id}>
                  {option.name}
                </li>
              );
            }}
          />
        );
      }}
    />
  );
};
